// Settings module translations - English
export default {
  title: 'Settings',
  subtitle: 'Personalize your app experience',
  
  // Setting groups
  groups: {
    privacySecurity: 'Privacy & Security',
    appSettings: 'App Settings',
    personalData: 'Personal Data',
    helpSupport: 'Help & Support'
  },
  
  // Setting items
  items: {
    privacySettings: {
      title: 'Privacy Settings & Policy',
      subtitle: 'AI features, data usage control'
    },
    privacyCollection: {
      title: 'Personal Data Collection',
      subtitle: 'See how we use your data'
    },
    appSettings: {
      title: 'App Settings',
      subtitle: 'Customize app behavior'
    },
    notificationSettings: {
      title: 'Notification Settings',
      subtitle: 'Manage push notification preferences'
    },
    languageSettings: {
      title: 'Language Settings',
      subtitle: 'Choose your preferred language',
      sectionTitle: 'Select Language',
      sectionSubtitle: 'Choose your preferred language',
      note: 'Language changes will take effect immediately without restarting the app'
    },
    bodyDataSetup: {
      title: 'Body Data Setup',
      subtitle: 'Configure health data parameters'
    },
    helpFeedback: {
      title: 'Help & Feedback',
      subtitle: 'Get help or provide suggestions',
      appTitle: 'CubeMeet',
      appSlogan: 'Share Excitement, Gather Happiness',
      appDescription: 'A comprehensive social group-buying platform supporting various group activities and smart features.',
      coreFeatures: 'Core Features',
      contactSupport: 'Contact Support',
      emailFeedback: 'Email Feedback',
      emailFeedbackDesc: 'Send us an email',
      features: {
        travel: { name: 'Travel', desc: 'Smart travel expense sharing, AI receipt recognition' },
        carpool: { name: 'Carpool', desc: 'Green travel, eco-friendly ridesharing' },
        dining: { name: 'Dining', desc: 'Event gatherings and dining' },
        shopping: { name: 'Shopping', desc: 'Second-hand trading, local marketplace' },
        fitness: { name: 'Fitness', desc: 'Body management, health tracking' },
        todo: { name: 'Todo', desc: 'Task management, collaborative lists' },
        ledger: { name: 'Ledger', desc: 'Personal and cloud accounting' },
        groupBuying: { name: 'Group Buy', desc: 'Group orders, food delivery sharing' },
        toolbox: { name: 'Toolbox', desc: 'AI smart tools, intelligent recipes' }
      }
    }
  },
  
  // Messages
  messages: {
    logoutConfirm: 'Are you sure you want to logout?',
  deleteAccount: 'Delete Account',
    deleteAccountConfirm: 'Deleting your account cannot be undone. Are you sure?',
  deleteAccountNotice: 'Your account will be immediately disabled and you will be signed out. All data will be permanently deleted from our servers on {date}. During this period, you can contact support to reverse this action.',
  deleteAccountDisabledToast: 'Your account has been disabled. All data will be permanently deleted on {date}. If you need to restore your account, contact support@cubemeet.app before {date}.',
    settingsSaved: 'Settings saved',
    settingsFailed: 'Failed to save settings'
  },
  
  // Privacy Settings Screen
  privacyScreen: {
    title: 'Privacy Settings & Policy',
  infoTitle: 'Privacy Settings & Policy',
  infoDescription: 'Control AI features, data sync and privacy-related settings here to protect your personal information.',
    privacyProtection: 'Privacy Protection',
    privacyDescription: 'We respect your privacy and you have complete control over how your data is used. These settings determine how the app handles your personal information.',
    aiFeaturesControl: 'AI Features Control',
    aiPrivacyControl: 'AI Privacy Control',
    aiPrivacySubtitle: 'Control the use of AI technology in the app to protect your data privacy',
    enableAiFeatures: 'Enable AI Features',
    aiFeatureDescription: 'Including text recognition, food recognition, smart recommendations and other AI features',
    dataUsageSecurity: 'Data Usage Security',
    dataPrivacyControl: 'Data Privacy Control',
    dataPrivacySubtitle: 'Control cloud storage and sharing of data to ensure your privacy security',
    cloudDataSharing: 'Cloud Data Sharing',
    cloudSharingDescription: 'Allow creating shared ledgers, data will be synced to cloud for collaboration with others',
    dataUsageExplanation: 'Data Usage Explanation',
    dataUsagePoint1: 'AI processed data is only used to provide corresponding functions',
    dataUsagePoint2: 'Data transmission uses encrypted protection',
    dataUsagePoint3: 'Will not store your personal privacy information',
    dataUsagePoint4: 'You can turn off any feature at any time',
    privacyPolicyAndTerms: 'Privacy Policy & Terms',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    futurePrivacySettings: 'More Privacy Settings',
    futurePrivacyDescription: 'Future versions will add more privacy control options such as shared ledgers, data synchronization, etc.'
  },

  // Privacy Policy Screen
  privacyPolicyScreen: {
    title: 'Privacy Policy',
    dataCollection: 'Data Collection',
    dataCollectionContent: 'We are committed to protecting your privacy and personal information security. The information collected by this app is only used to provide better service experience and will not be used for other commercial purposes.',
    informationUse: 'Information Use',
    informationUseContent: '• Your personal information is only used for the normal operation of app functions\n• We will not sell or share your personal information to third parties\n• All data transmission is encrypted',
    dataSecurity: 'Data Security',
    dataSecurityContent: 'We employ industry-standard security measures to protect your personal information, including data encryption, secure transmission protocols, etc.',
    userRights: 'User Rights',
    userRightsContent: 'You have the right to at any time:\n• View information we collect about you\n• Request correction or deletion of your personal information\n• Withdraw consent for data processing\n• Turn off any feature or delete account at any time',
    contactUs: 'Contact Us',
    contactUsContent: 'If you have any questions about this privacy policy, please contact us:\nEmail: privacy@cubemeet.com',
    lastUpdated: 'Last Updated: January 1, 2025'
  },

  // Terms of Service Screen
  termsOfServiceScreen: {
    title: 'Terms of Service',
    acceptanceOfTerms: 'Acceptance of Terms',
    acceptanceContent: 'By using this app, you agree to comply with the following terms of service. If you do not agree with these terms, please do not use this app.',
    serviceDescription: 'Service Description',
    serviceDescriptionContent: 'CubeMeet is a social life app that helps users:\n• Manage personal and shared ledgers\n• Organize group activities like travel, dining, carpooling\n• Share life moments with friends\n• Use AI smart features to enhance experience',
    userResponsibilities: 'User Responsibilities',
    userResponsibilitiesContent: 'When using this app, you agree to:\n• Provide accurate and truthful information\n• Not violate laws and regulations\n• Not infringe on others\' rights\n• Not engage in malicious behavior or abuse services',
    serviceLimitations: 'Service Limitations',
    serviceLimitationsContent: 'We reserve the right to limit or terminate services in the following situations:\n• Violation of terms of service\n• Technical maintenance requirements\n• Legal and regulatory requirements\n• Other reasonable reasons',
    disclaimer: 'Disclaimer',
    disclaimerContent: 'This app provides services "as is" and we are not responsible for:\n• Service interruptions or data loss\n• Third-party service issues\n• Disputes between users\n• Force majeure factors',
    termsModification: 'Terms Modification',
    termsModificationContent: 'We reserve the right to modify these terms at any time. Major changes will be notified to users through in-app notifications. Continued use of the app indicates your acceptance of the modified terms.',
    contactUs: 'Contact Us',
    contactUsContent: 'If you have any questions about these terms of service, please contact us:\nEmail: my.cubemeet@gmail.com',
    lastUpdated: 'Last Updated: January 1, 2025'
  }
};
